import { cn } from "@/utils";

/**
 * The initials avatar, in one place.
 *
 * There were two, and they did not match. The account shell drew a 28px circle
 * from the `brand-cyan` Tailwind tokens; the profile page drew a larger one
 * from inline styles with a hardcoded `var(--acc2)` and a cyan-to-violet gradient
 * that exists nowhere else in the app. Same element, same job, two looks — and
 * one of them could not follow a theme change because its colour was a literal.
 *
 * Initials are derived here rather than taken as a prop, because that was the
 * other half of the duplication: the shell got them from its auth context and
 * the profile page recomputed them with its own `?.[0]` expression. One
 * derivation means one answer to the awkward cases — a single name, a missing
 * surname, an empty string.
 */

export interface AvatarInitialsProps {
  /** Shown when both name parts are missing; also the accessible name source. */
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZES = {
  sm: "h-7 w-7 text-[11px]",
  md: "h-10 w-10 text-sm",
  lg: "h-16 w-16 text-xl",
} as const;

/**
 * First letter of the first two words, uppercased.
 *
 * Falls back to "U" rather than rendering an empty circle: a blank disc reads
 * as a broken image, where a letter reads as a person without a name on file.
 */
export function toInitials(
  firstName?: string | null,
  lastName?: string | null,
  name?: string | null,
): string {
  const first = (firstName ?? "").trim();
  const last = (lastName ?? "").trim();
  if (first || last) {
    return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase() || "U";
  }
  // One free-text name: take the first letter of its first two words, so
  // "Jacob Christian" gives JC and "Jacob" gives J rather than JA.
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  const derived = parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("");
  return derived.toUpperCase() || "U";
}

export default function AvatarInitials({
  name,
  firstName,
  lastName,
  size = "sm",
  className,
}: AvatarInitialsProps) {
  const initials = toInitials(firstName, lastName, name);
  const full = [firstName, lastName].filter(Boolean).join(" ") || name || null;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full",
        "border border-brand-cyan/30 bg-brand-cyan/10 font-bold text-brand-cyan",
        SIZES[size],
        className,
      )}
      // The initials are a compressed form of a name that is almost always
      // already on screen beside them, so they are decorative by default. Where
      // a name IS known and not otherwise present, it becomes the label rather
      // than leaving a screen reader to spell out two letters.
      {...(full
        ? { role: "img", "aria-label": full }
        : { "aria-hidden": true as const })}
    >
      {initials}
    </span>
  );
}
